import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { ServiceLabel } from "@/types/services";

interface ServiceCardProps {
  service: any;
  showLabels?: boolean;
  className?: string;
}

const ServiceCard = ({ service, showLabels = true, className = "" }: ServiceCardProps) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <Card className={`group bg-white dark:bg-nexus-dark border border-gray-200 transition-all hover:shadow-md hover:border-nexus-teal/40 ${className}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between mb-4">
          <div className={`w-12 h-12 rounded-lg ${service.bgColor} flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
            <service.icon className={`h-6 w-6 ${service.color}`} />
          </div>
          {showLabels && (
            <div className="flex gap-1 flex-wrap">
              {service.labels.map((label, labelIndex) => (
                <Badge 
                  key={labelIndex}
                  variant={label === ServiceLabel.BASIC ? "default" : "secondary"}
                  className={
                    label === ServiceLabel.BASIC  ? "bg-green-100 text-green-800" : 
                    label === ServiceLabel.ADVANCED? "bg-orange-100 text-orange-800" :
                    label === ServiceLabel.PREMIUM ? "bg-purple-100 text-purple-800" :
                    "bg-blue-100 text-blue-800"
                  }
                >
                   {t(`services_labels.${label.toLowerCase()}`)}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="w-full">
          <CardTitle className="text-xl text-grey-600">{service.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-grey-600 mb-4">
          {service.description}
        </CardDescription>
        <div className={`w-full ${isRTL ? 'text-left' : 'text-right'}`}>
          <Link 
            to={{ pathname:`/services/${service.id}`}} 
            className="text-nexus-teal font-medium inline-flex items-center hover:text-nexus-teal/80 transition-colors"
          >
            {language === 'ar' ? (
              <>
                {t("services_section.learnMore")}
                <ArrowRight className="mr-2 h-4 w-4 transform rotate-180 transition-transform duration-300 group-hover:-translate-x-1" />
              </>
            ) : (
              <>
                {t("services_section.learnMore")}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceCard
